import { Button } from "@/components/ui/button.tsx";
import { Label } from "@/components/ui/label.tsx";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover.tsx";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select.tsx";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip.tsx";
import { api } from "@/lib/http.ts";
import { cn } from "@/lib/utils.ts";
import { updateThreadOptimistically } from "@/hooks/useThreads.tsx";
import { useCurrentUserContext } from "@/hooks/useUser.tsx";
import {
  AGENT_VERSIONS,
  AGENT_VERSION_LABELS,
  type AgentVersion,
  ANTHROPIC_EFFORT_LEVELS,
  DEFAULT_MODEL_BY_PROVIDER,
  MODEL_PROVIDERS,
  MODEL_PROVIDER_LABELS,
  MODELS_BY_PROVIDER,
  type ModelPreferenceDefaults,
  type ModelPreferences,
  type ModelProvider,
  PROVIDER_MODEL_LABELS,
  type ProviderModel,
  THINKING_TOKEN_BUDGET_LEVELS,
  type ThinkingLevel,
} from "@/types/modelPreferences.ts";
import type {
  ThreadConfig,
  ThreadConfigDefaults,
  UpdateThreadConfigResp,
} from "@/types/threadConfig.ts";
import type { GetThreadResp } from "@/types/thread.ts";
import { useGateValue } from "@statsig/react-bindings";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Cpu } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { UI_CONSTANTS } from "./constants.ts";
import { pickerModel } from "./pickerModel.ts";

type ModelSwitcherProps = {
  /**
   * Existing thread to read/write config on. Undefined means we're in the
   * home-page omnibox composing a brand-new thread — we buffer the choice
   * in local state and surface it via `onConfigChange` so the new-thread
   * create call can seed `config` at thread creation.
   */
  thread?: GetThreadResp;
  /**
   * Lift handler for home-page mode — fires whenever the user picks an
   * option pre-thread. Ignored on the thread page (writes go straight
   * to the server there).
   */
  onConfigChange?: (config: ThreadConfig) => void;
  /**
   * The control that opens the picker, for a host with somewhere specific to
   * put it: the composer's tools menu wants a labelled row rather than the
   * naked glyph this renders on a toolbar. Absent, the glyph is the trigger.
   */
  trigger?: React.ReactNode;
};

export const ModelSwitcher = ({ thread, onConfigChange, trigger }: ModelSwitcherProps) => {
  const { userContext } = useCurrentUserContext();
  const modelPickerEnabled = useGateValue("model_picker");

  if (!modelPickerEnabled || userContext === undefined) {
    return null;
  }

  // Defaults always come from the canonical /me response — single source of
  // truth for the cohort-driven actor model + the static `"v4"` agent default.
  const defaults = userContext.threadConfigDefaults;

  if (thread === undefined) {
    return (
      <HomeModeSwitcher defaults={defaults} onConfigChange={onConfigChange} trigger={trigger} />
    );
  }
  return <ThreadModeSwitcher thread={thread} defaults={defaults} trigger={trigger} />;
};

// -----------------------------------------------------------------------------
// Thread-page mode: read existing config from the thread prop, write through
// the POST endpoint and refresh the cached thread on success.
// -----------------------------------------------------------------------------

const ThreadModeSwitcher = ({
  thread,
  defaults,
  trigger,
}: {
  thread: GetThreadResp;
  defaults: ThreadConfigDefaults;
  trigger?: React.ReactNode;
}) => {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: (patch: ThreadConfig) =>
      api
        .post<UpdateThreadConfigResp>(`/api/a/threads/${thread.id}/config`, patch)
        .then((res) => res.data),
    onSuccess: (resp) => {
      // Patch the cached thread so the next render sees the new config without
      // a roundtrip GET. Must use the helper's canonical key
      // (`["threads","detail",id]`) — a raw `["thread", id]` write no-ops.
      updateThreadOptimistically(queryClient, thread.id, { config: resp.config });
    },
    onError: () => toast.error("Couldn't update thread settings"),
  });

  const prefs = thread.config?.modelPreferences ?? {};
  const effective = effectiveModelPreferences(prefs, defaults);

  return (
    <SwitcherShell hasOverrides={hasAnyOverride(prefs)} trigger={trigger}>
      <SwitcherBody
        provider={effective.provider}
        agentVersion={effective.agentVersion}
        model={effective.model}
        thinkingTokenBudget={effective.thinkingTokenBudget}
        onProviderChange={(provider) =>
          // Reset model to the new provider's default in the same patch —
          // otherwise we could persist `{ gemini, opus }` which the schema
          // would reject on the next write. Clamp the thinking level too so
          // we don't leave e.g. `xhigh` selected after switching to Gemini.
          mutation.mutate({
            modelPreferences: {
              provider,
              model: DEFAULT_MODEL_BY_PROVIDER[provider],
              thinkingTokenBudget: clampThinkingLevel(effective.thinkingTokenBudget, provider),
            },
          })
        }
        onAgentVersionChange={(agentVersion) =>
          mutation.mutate({ modelPreferences: { agentVersion } })
        }
        // Always store provider alongside model. The picker mapping on the
        // backend keys on `prefs.provider`, so a model-only patch on a row
        // that never explicitly saved a provider would write `{ model }`
        // alone — and the mapping would silently no-op back to the code
        // default.
        onModelChange={(model) =>
          mutation.mutate({ modelPreferences: { provider: effective.provider, model } })
        }
        onThinkingTokenBudgetChange={(thinkingTokenBudget) =>
          mutation.mutate({ modelPreferences: { thinkingTokenBudget } })
        }
      />
    </SwitcherShell>
  );
};

// -----------------------------------------------------------------------------
// Home-page mode: no thread yet — buffer in local state, lift to parent.
// -----------------------------------------------------------------------------

const HomeModeSwitcher = ({
  defaults,
  onConfigChange,
  trigger,
}: {
  defaults: ThreadConfigDefaults;
  onConfigChange?: (config: ThreadConfig) => void;
  trigger?: React.ReactNode;
}) => {
  const [prefs, setPrefs] = useState<ModelPreferences>({});
  const effective = effectiveModelPreferences(prefs, defaults);

  // Buffer the model preferences and lift the config so the new-thread create
  // call seeds them.
  const emit = (nextPrefs: ModelPreferences) => {
    setPrefs(nextPrefs);
    onConfigChange?.({ modelPreferences: nextPrefs });
  };

  return (
    <SwitcherShell hasOverrides={hasAnyOverride(prefs)} trigger={trigger}>
      <SwitcherBody
        provider={effective.provider}
        agentVersion={effective.agentVersion}
        model={effective.model}
        thinkingTokenBudget={effective.thinkingTokenBudget}
        onProviderChange={(provider) =>
          emit({
            ...prefs,
            provider,
            model: DEFAULT_MODEL_BY_PROVIDER[provider],
            thinkingTokenBudget: clampThinkingLevel(effective.thinkingTokenBudget, provider),
          })
        }
        onAgentVersionChange={(agentVersion) => emit({ ...prefs, agentVersion })}
        onModelChange={(model) => emit({ ...prefs, provider: effective.provider, model })}
        onThinkingTokenBudgetChange={(thinkingTokenBudget) =>
          emit({ ...prefs, thinkingTokenBudget })
        }
      />
    </SwitcherShell>
  );
};

const hasAnyOverride = (prefs: ModelPreferences): boolean =>
  !!prefs.provider || !!prefs.agentVersion || !!prefs.model || !!prefs.thinkingTokenBudget;

/** The thinking levels a provider offers: Anthropic its native effort vocab, the rest the ladder. */
const thinkingLevelsFor = (provider: ModelProvider): readonly ThinkingLevel[] =>
  provider === "anthropic" ? ANTHROPIC_EFFORT_LEVELS : THINKING_TOKEN_BUDGET_LEVELS;

/**
 * Clamp a level to the provider's vocab: the level itself if valid, else the
 * nearest equivalent — top-of-vocab maps to top-of-vocab (`highest` ↔ `max`,
 * `xhigh` → `highest`), and everything else floors to `low`. A level can be
 * out-of-range for a number of reasons (a provider switch, a stale or legacy
 * stored value, …), so we always coerce rather than assume it's valid.
 */
const clampThinkingLevel = (level: ThinkingLevel, provider: ModelProvider): ThinkingLevel => {
  if (thinkingLevelsFor(provider).includes(level)) return level;
  switch (level) {
    case "xhigh":
    case "max":
      return "highest";
    case "highest":
      return "max";
    default:
      return "low";
  }
};

// -----------------------------------------------------------------------------
// Shared shell + body
// -----------------------------------------------------------------------------

const SwitcherShell = ({
  hasOverrides,
  trigger,
  children,
}: {
  hasOverrides: boolean;
  /**
   * The control that opens it. Given by a host that has somewhere specific to
   * put it — the composer's tools menu wants a labelled row, not the naked
   * glyph this renders on a toolbar. Absent, the glyph is the trigger.
   */
  trigger?: React.ReactNode;
  children: React.ReactNode;
}) =>
  trigger ? (
    <Popover>
      <PopoverTrigger asChild>{trigger}</PopoverTrigger>
      <PopoverContent className="w-64 p-3" align="start">
        <div className="space-y-3">{children}</div>
      </PopoverContent>
    </Popover>
  ) : (
    <Popover>
      <Tooltip>
        <TooltipTrigger asChild>
          <PopoverTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              data-statsig-name="omnibox-model-switcher"
              className={`${UI_CONSTANTS.BUTTON_SIZE} ${
                hasOverrides
                  ? "bg-amber-500/20 text-amber-600 hover:bg-amber-500/30"
                  : "bg-secondary hover:bg-secondary/80"
              }`}
            >
              <Cpu className={cn(UI_CONSTANTS.ICON_SIZE, !hasOverrides && "text-blue-600")} />
            </Button>
          </PopoverTrigger>
        </TooltipTrigger>
        <TooltipContent side="top">Configure model + agent for this thread</TooltipContent>
      </Tooltip>
      <PopoverContent className="w-64 p-3" align="start">
        <div className="space-y-3">{children}</div>
      </PopoverContent>
    </Popover>
  );

/**
 * `local` points at an OpenAI-compatible server on the developer's own machine,
 * so no deployed environment can serve it. An already-selected `local` stays
 * listed so the trigger doesn't render blank.
 */
const providerOptions = (selected: ModelProvider): readonly ModelProvider[] =>
  MODEL_PROVIDERS.filter((p) => p !== "local" || import.meta.env.DEV || selected === "local");

const SwitcherBody = ({
  provider,
  agentVersion,
  model,
  thinkingTokenBudget,
  onProviderChange,
  onAgentVersionChange,
  onModelChange,
  onThinkingTokenBudgetChange,
}: {
  provider: ModelProvider;
  agentVersion: AgentVersion;
  model: ProviderModel;
  thinkingTokenBudget: ThinkingLevel;
  onProviderChange: (provider: ModelProvider) => void;
  onAgentVersionChange: (agentVersion: AgentVersion) => void;
  onModelChange: (model: ProviderModel) => void;
  onThinkingTokenBudgetChange: (thinkingTokenBudget: ThinkingLevel) => void;
}) => (
  <>
    <SwitcherRow label="Model Provider">
      <Select value={provider} onValueChange={(v) => onProviderChange(v as ModelProvider)}>
        <SelectTrigger className="h-8 text-xs" data-statsig-name="omnibox-model-switcher-provider">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {providerOptions(provider).map((p) => (
            <SelectItem key={p} value={p} className="text-xs">
              {MODEL_PROVIDER_LABELS[p]}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </SwitcherRow>
    <SwitcherRow label="Model">
      <Select value={model} onValueChange={(v) => onModelChange(v as ProviderModel)}>
        <SelectTrigger className="h-8 text-xs" data-statsig-name="omnibox-model-switcher-model">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {MODELS_BY_PROVIDER[provider].map((m) => (
            <SelectItem key={m} value={m} className="text-xs">
              {PROVIDER_MODEL_LABELS[m]}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </SwitcherRow>
    <SwitcherRow label="Thinking Budget">
      <Select
        value={thinkingTokenBudget}
        onValueChange={(v) => onThinkingTokenBudgetChange(v as ThinkingLevel)}
      >
        <SelectTrigger
          className="h-8 text-xs"
          data-statsig-name="omnibox-model-switcher-thinking-budget"
        >
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {thinkingLevelsFor(provider).map((level) => (
            <SelectItem key={level} value={level} className="text-xs capitalize">
              {level}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </SwitcherRow>
    {AGENT_VERSIONS.length > 1 && (
      <SwitcherRow label="Agent Version">
        <Select value={agentVersion} onValueChange={(v) => onAgentVersionChange(v as AgentVersion)}>
          <SelectTrigger
            className="h-8 text-xs"
            data-statsig-name="omnibox-model-switcher-agent-version"
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {AGENT_VERSIONS.map((v) => (
              <SelectItem key={v} value={v} className="text-xs">
                {AGENT_VERSION_LABELS[v]}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </SwitcherRow>
    )}
  </>
);

const SwitcherRow = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <div className="space-y-1">
    <Label className="text-xs text-muted-foreground">{label}</Label>
    <div>{children}</div>
  </div>
);

const effectiveModelPreferences = (
  prefs: ModelPreferences,
  defaults: ThreadConfigDefaults,
): ModelPreferenceDefaults => {
  const provider = prefs.provider ?? defaults.modelPreferences.provider;
  const model = pickerModel(provider, prefs.model, defaults.modelPreferences);
  return {
    provider,
    agentVersion: prefs.agentVersion ?? defaults.modelPreferences.agentVersion,
    model,
    thinkingTokenBudget: clampThinkingLevel(
      prefs.thinkingTokenBudget ?? defaults.modelPreferences.thinkingTokenBudget,
      provider,
    ),
  };
};
